import { useState, useEffect } from 'react';
import { apiFetch } from '../shared/api.ts';
import { Cargando, ErrorApi } from '../shared/ui.tsx';
import { formatoRango } from '../shared/horas.ts';
import { QRCodeSVG } from 'qrcode.react';

type Cliente = {
  id: string;
  nombre: string;
  salones_count: number;
};

type QrData = {
  cliente_id: string;
  min: number;
  hmac: string;
};

type Salon = {
  id: string;
  nombre: string;
  radio_metros: number;
};

type Convocatoria = {
  convocatoria_id: string;
  persona_nombre: string;
  persona_apellido: string;
  categoria_nombre: string;
  salon_nombre: string;
  hora_inicio: string;
  hora_fin: string;
};

type Checkin = {
  persona_nombre: string;
  persona_apellido: string;
  salon_nombre: string;
  checkin_hora: string;
};

type QrActual = {
  cliente_id: string;
  cliente_nombre: string;
  salones: Salon[];
  qr: QrData;
  convocatorias: Convocatoria[];
  checkins: Checkin[];
};

function Icono({ d, className = 'h-5 w-5' }: { d: string; className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.8} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d={d} />
    </svg>
  );
}

export function QrAdminPage() {
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [clienteActivo, setClienteActivo] = useState<string | null>(null);
  const [qrData, setQrData] = useState<QrActual | null>(null);
  const [cargandoQr, setCargandoQr] = useState(false);
  const [segundosRestantes, setSegundosRestantes] = useState(60);

  useEffect(() => {
    apiFetch<Cliente[]>('/api/clientes')
      .then(setClientes)
      .catch((e) => setError(e instanceof Error ? e.message : 'Error'))
      .finally(() => setCargando(false));
  }, []);

  useEffect(() => {
    if (!clienteActivo) return;
    setCargandoQr(true);
    apiFetch<QrActual>(`/api/cliente/${clienteActivo}/qr-actual`)
      .then((data) => { setQrData(data); setSegundosRestantes(60); })
      .catch(() => setQrData(null))
      .finally(() => setCargandoQr(false));
  }, [clienteActivo]);

  useEffect(() => {
    if (!clienteActivo) return;
    const interval = setInterval(() => {
      apiFetch<QrActual>(`/api/cliente/${clienteActivo}/qr-actual`)
        .then((data) => { setQrData(data); setSegundosRestantes(60); })
        .catch(() => {});
    }, 30000);
    return () => clearInterval(interval);
  }, [clienteActivo]);

  useEffect(() => {
    if (!clienteActivo) return;
    const timer = setInterval(() => {
      setSegundosRestantes(s => (s <= 1 ? 60 : s - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [clienteActivo]);

  const qrPct = Math.round((segundosRestantes / 60) * 100);
  const qrExpirando = segundosRestantes <= 10;

  if (cargando) return <Cargando />;
  if (error) return <ErrorApi mensaje={error} />;

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-xl font-bold text-neutro-800">Códigos QR</h2>
        <p className="text-sm text-neutro-500 mt-0.5">Visualizá los QR de cada cliente. El personal los escanea para hacer check-in.</p>
      </div>

      {/* Client cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {clientes.map((c) => (
          <button
            key={c.id}
            onClick={() => setClienteActivo(clienteActivo === c.id ? null : c.id)}
            className={`tarjeta text-left transition-all ${clienteActivo === c.id ? 'ring-2 ring-primario-400 border-primario-400' : 'tarjeta-interactiv'}`}
          >
            <div className="flex items-center gap-3">
              <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${clienteActivo === c.id ? 'bg-primario-400 text-white' : 'bg-superficie-200 text-neutro-500'}`}>
                <Icono d="M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21" className="h-5 w-5" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-neutro-800 truncate">{c.nombre}</p>
                <p className="text-xs text-neutro-400">{c.salones_count} salón{c.salones_count !== 1 ? 'es' : ''}</p>
              </div>
              <Icono d={clienteActivo === c.id ? 'm19.5 8.25-7.5 7.5-7.5-7.5' : 'm8.25 4.5 7.5 7.5-7.5 7.5'} className="h-4 w-4 text-neutro-400" />
            </div>
          </button>
        ))}
      </div>

      {/* QR detail */}
      {clienteActivo && (
        <div className="rounded-2xl border border-contorno-var bg-white overflow-hidden">
          <div className="bg-primario-400 px-5 py-4">
            <p className="font-semibold text-white">{qrData?.cliente_nombre ?? 'Cargando…'}</p>
            <p className="text-sm text-white/70">QR dinámico — se regenera cada minuto</p>
          </div>
          <div className="p-5">
            {cargandoQr ? (
              <div className="py-8"><Cargando /></div>
            ) : qrData ? (
              <div className="flex flex-col lg:flex-row gap-6">
                {/* QR */}
                <div className="flex flex-col items-center gap-4 shrink-0">
                  <div className="relative">
                    <div className="rounded-2xl bg-white p-4 shadow-sm border border-contorno-var">
                      <QRCodeSVG value={JSON.stringify(qrData.qr)} size={180} level="M" bgColor="#ffffff" fgColor="#261813" />
                    </div>
                    <div className="absolute inset-0 rounded-2xl overflow-hidden pointer-events-none">
                      <div className="absolute inset-x-4 top-0 h-0.5 bg-primario-400 animate-[scan_2s_ease-in-out_infinite]" />
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="relative h-10 w-10">
                      <svg className="h-10 w-10 -rotate-90" viewBox="0 0 40 40">
                        <circle cx="20" cy="20" r="17" fill="none" stroke="#fde3da" strokeWidth="3" />
                        <circle cx="20" cy="20" r="17" fill="none" stroke="#f26522" strokeWidth="3" strokeDasharray="106.8" strokeDashoffset={106.8 * (1 - qrPct / 100)} strokeLinecap="round" className="transition-all duration-1000" />
                      </svg>
                      <span className={`absolute inset-0 flex items-center justify-center text-xs font-bold ${qrExpirando ? 'text-red-500 animate-pulse' : 'text-neutro-600'}`}>
                        {segundosRestantes}s
                      </span>
                    </div>
                    <span className="text-xs text-neutro-500">{qrExpirando ? 'Expira pronto' : 'Se regenera automáticamente'}</span>
                  </div>
                </div>

                {/* Info */}
                <div className="flex-1 space-y-4">
                  {qrData.salones.length > 0 && (
                    <div>
                      <h3 className="text-sm font-semibold text-neutro-800 mb-2">Salones ({qrData.salones.length})</h3>
                      <ul className="space-y-1.5">
                        {qrData.salones.map(sal => (
                          <li key={sal.id} className="flex items-center justify-between rounded-lg bg-superficie px-3 py-2 text-sm">
                            <span className="font-medium text-neutro-700">{sal.nombre}</span>
                            <span className="text-xs text-neutro-400">Radio: {sal.radio_metros}m</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {qrData.convocatorias.length > 0 && (
                    <div>
                      <h3 className="text-sm font-semibold text-neutro-800 mb-2">Esperados hoy ({qrData.convocatorias.length})</h3>
                      <ul className="space-y-1.5">
                        {qrData.convocatorias.map(c => (
                          <li key={c.convocatoria_id} className="flex items-center justify-between rounded-lg bg-superficie px-3 py-2 text-sm">
                            <div className="min-w-0">
                              <span className="font-medium text-neutro-700">{c.persona_apellido} {c.persona_nombre}</span>
                              <span className="ml-2 text-xs text-neutro-400">{c.categoria_nombre}</span>
                            </div>
                            <span className="text-xs text-neutro-400 shrink-0 ml-2">{c.salon_nombre} · {formatoRango(c.hora_inicio, c.hora_fin)}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {qrData.checkins.length > 0 && (
                    <div>
                      <h3 className="text-sm font-semibold text-estado-ok mb-2">Check-ins realizados ({qrData.checkins.length})</h3>
                      <ul className="space-y-1.5">
                        {qrData.checkins.map((ch, i) => (
                          <li key={i} className="flex items-center justify-between rounded-lg bg-green-50 border border-green-200 px-3 py-2 text-sm">
                            <span className="font-medium text-neutro-700">{ch.persona_apellido} {ch.persona_nombre}</span>
                            <span className="text-xs text-neutro-400">{ch.salon_nombre} · {new Date(ch.checkin_hora).toLocaleTimeString('es-UY', { hour: '2-digit', minute: '2-digit' })}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {qrData.salones.length === 0 && qrData.convocatorias.length === 0 && qrData.checkins.length === 0 && (
                    <p className="text-sm text-neutro-400 text-center py-4">Sin datos para hoy</p>
                  )}
                </div>
              </div>
            ) : (
              <p className="text-sm text-red-500 text-center py-4">No se pudo generar el QR</p>
            )}
          </div>
        </div>
      )}

      <style>{`
        @keyframes scan {
          0%, 100% { transform: translateY(0); opacity: 0.5; }
          50% { transform: translateY(180px); opacity: 1; }
        }
      `}</style>
    </div>
  );
}
